import React from 'react';
import { Badge, Button } from 'react-bootstrap';

import { Colors } from '_styles';

import { formatFlight, formatDateTime } from './format';

const NOTES_PREVIEW_LENGTH = 80;

// Where one airport stands on a legacy-app booking. Read-only: airports confirm these in the portal.
const AirportStatus = ({ label, required, confirmed }) => (
  <div>
    <div style={styles.heading}>{label}</div>
    {!required ? (
      <span style={styles.notRequired}>Not required</span>
    ) : confirmed ? (
      <span style={styles.confirmed}>✅ Confirmed</span>
    ) : (
      <span style={styles.awaiting}>Awaiting airport</span>
    )}
  </div>
);

// One legacy-app booking. Every passenger-supplied value is rendered as React text.
const LegacyRow = ({ booking, expanded, onToggleNotes, onReject, rejecting, onEditNote, onConfirm, confirming }) => {
  const notes = typeof booking.bio === 'string' ? booking.bio.trim() : '';
  const longNotes = notes.length > NOTES_PREVIEW_LENGTH;
  const shownNotes = longNotes && !expanded ? `${notes.slice(0, NOTES_PREVIEW_LENGTH)}…` : notes;

  const assistance = Array.isArray(booking.assistance) ? [...new Set(booking.assistance)] : [];

  return (
    <tr>
      {/* passenger */}
      <td style={styles.cell}>
        <div style={styles.name}>{booking.name || '—'}</div>
        {booking.email ? <div style={{ ...styles.sub, wordBreak: 'break-all' }}>{booking.email}</div> : null}
        {booking.rejected ? <Badge bg="danger" style={{ fontSize: 11, marginTop: 4 }}>Rejected</Badge> : null}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
          {assistance.map((code) => (
            <Badge key={code} bg="primary" style={{ fontSize: 11 }}>{code}</Badge>
          ))}
        </div>
      </td>

      {/* notes */}
      <td style={{ ...styles.cell, maxWidth: 240 }}>
        {notes ? (
          <>
            <div style={styles.notes}>{shownNotes}</div>
            {longNotes ? (
              <Button variant="link" size="sm" style={styles.linkButton} onClick={() => onToggleNotes(booking.id)}>
                {expanded ? 'Show less' : 'Show more'}
              </Button>
            ) : null}
          </>
        ) : (
          <span style={styles.muted}>—</span>
        )}
        {booking.internalNote ? (
          <div style={styles.internalNote}>
            <div style={styles.internalLabel}>Internal note · not shown to passenger</div>
            <div style={styles.notes}>{booking.internalNote.text}</div>
            <div style={styles.sub}>
              {[booking.internalNote.updatedBy, formatDateTime(booking.internalNote.updatedAt, true)].filter(Boolean).join(' · ')}
            </div>
          </div>
        ) : null}
      </td>

      {/* flight + route */}
      <td style={styles.cell}>
        <div style={styles.value}>{formatFlight(booking.flight) || '—'}</div>
        {booking.flight?.airlineName ? <div style={styles.sub}>{booking.flight.airlineName}</div> : null}
        <div style={{ ...styles.value, marginTop: 4 }}>
          {`${booking.origin?.iata || ''} ${booking.origin?.name || ''}`.trim() || '—'}
          {' → '}
          {`${booking.destination?.iata || ''} ${booking.destination?.name || ''}`.trim() || '—'}
        </div>
      </td>

      {/* booking details */}
      <td style={styles.cell}>
        <div style={styles.sub}>Reference</div>
        <div style={styles.value}>{booking.referenceNumber || '—'}</div>
        <div style={{ ...styles.sub, marginTop: 4 }}>Booked</div>
        <div style={styles.value}>{formatDateTime(booking.createdAt, true) || '—'}</div>
      </td>

      {/* airport status */}
      <td style={styles.cell}>
        <AirportStatus
          label={booking.origin?.iata || 'Departure'}
          required={booking.required?.origin}
          confirmed={booking.originConfirmed}
        />
      </td>
      <td style={styles.cell}>
        <AirportStatus
          label={booking.destination?.iata || 'Arrival'}
          required={booking.required?.destination}
          confirmed={booking.destinationConfirmed}
        />
      </td>

      {/* actions */}
      <td style={styles.cell}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
          <Button variant="outline-secondary" size="sm" onClick={() => onEditNote(booking)}>
            {booking.internalNote ? 'Edit note' : 'Add note'}
          </Button>
          {!booking.rejected && booking.status !== 'confirmed' ? (
            <>
              <Button variant="primary" size="sm" disabled={confirming || rejecting} onClick={() => onConfirm(booking)}>
                {confirming ? 'Confirming…' : 'Confirm airports'}
              </Button>
              <Button variant="outline-danger" size="sm" disabled={rejecting || confirming} onClick={() => onReject(booking)}>
                {rejecting ? 'Rejecting…' : 'Reject'}
              </Button>
            </>
          ) : null}
        </div>
      </td>
    </tr>
  );
};

const styles = {
  cell: {
    verticalAlign: 'top',
    paddingTop: 12,
    paddingBottom: 12,
  },
  name: {
    fontSize: 14,
    fontWeight: 600,
    color: Colors.TEXT_COLOR,
  },
  value: {
    fontSize: 14,
    opacity: 0.8,
    color: Colors.TEXT_COLOR,
  },
  sub: {
    fontSize: 12,
    opacity: 0.55,
    color: Colors.TEXT_COLOR,
  },
  notes: {
    fontSize: 13,
    opacity: 0.8,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: Colors.TEXT_COLOR,
  },
  muted: {
    opacity: 0.4,
  },
  internalNote: {
    marginTop: 8,
    padding: '6px 8px',
    background: '#FFF8E1',
    borderLeft: '3px solid #B9770E',
    borderRadius: 4,
  },
  internalLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#B9770E',
    marginBottom: 2,
  },
  linkButton: {
    padding: 0,
    fontSize: 12,
  },
  heading: {
    fontSize: 12,
    opacity: 0.6,
    fontWeight: 'bold',
    marginBottom: 4,
    color: Colors.TEXT_COLOR,
    whiteSpace: 'nowrap',
  },
  notRequired: {
    fontSize: 13,
    color: '#999',
    fontStyle: 'italic',
  },
  confirmed: {
    fontSize: 13,
    color: '#2E7D32',
    fontWeight: 600,
  },
  awaiting: {
    fontSize: 13,
    color: '#B9770E',
    fontWeight: 600,
  },
};

export default LegacyRow;
